import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AnalysisState, AnalysisResult, EmailData, AnalysisHistoryItem } from '@/types/store';
import { v4 as uuidv4 } from 'uuid';
import logger from '@/services/logger';

const initialState: AnalysisState = {
  currentEmail: null,
  results: [],
  isAnalyzing: false,
  currentStepId: null,
  progress: 0,
  error: null,
  history: [],
};

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    setCurrentEmail: (state, action: PayloadAction<EmailData>) => {
      state.currentEmail = action.payload;
      logger.info('Current email set for analysis', { 
        module: 'analysis',
        data: {
          subject: action.payload.subject,
          from: action.payload.from,
        }
      });
    },
    
    clearCurrentEmail: (state) => {
      state.currentEmail = null;
      logger.info('Current email cleared', { module: 'analysis' });
    },
    
    startAnalysis: (state) => {
      state.isAnalyzing = true;
      state.progress = 0;
      state.results = [];
      state.error = null;
      logger.info('Analysis started', { 
        module: 'analysis',
        data: {
          emailId: state.currentEmail?.id
        }
      });
    },
    
    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.currentStepId = action.payload;
      logger.info(`Analysis current step set: ${action.payload}`, { 
        module: 'analysis',
        data: {
          stepId: action.payload
        }
      });
    },
    
    updateProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    
    addResult: (state, action: PayloadAction<Omit<AnalysisResult, 'id' | 'timestamp'>>) => {
      const id = uuidv4();
      const timestamp = new Date().toISOString();
      
      const result: AnalysisResult = {
        ...action.payload,
        id,
        timestamp,
      };
      
      state.results.push(result);
      
      logger.info(`Analysis result added: ${result.stepName}`, {
        module: 'analysis',
        data: {
          stepId: result.stepId,
          resultId: id
        }
      });
    },
    
    setAnalysisError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isAnalyzing = false;
      
      logger.error(`Analysis error: ${action.payload}`, {
        module: 'analysis',
        data: {
          error: action.payload
        }
      });
    },
    
    completeAnalysis: (state, action: PayloadAction<{
      overallScore?: number;
      verdict?: 'safe' | 'suspicious' | 'malicious' | 'unknown';
    }>) => {
      state.isAnalyzing = false;
      state.progress = 100;
      
      if (state.currentEmail) {
        // Create a history item for this analysis
        const historyItem: AnalysisHistoryItem = {
          id: uuidv4(),
          emailId: state.currentEmail.id,
          emailSubject: state.currentEmail.subject,
          emailFrom: state.currentEmail.from,
          timestamp: new Date().toISOString(),
          results: [...state.results],
          overallScore: action.payload.overallScore,
          verdict: action.payload.verdict,
        };
        
        // Add to history (limited to last 50 items)
        state.history = [historyItem, ...state.history].slice(0, 50);
        
        logger.info('Analysis completed', {
          module: 'analysis',
          data: {
            emailId: state.currentEmail.id,
            verdict: action.payload.verdict,
            score: action.payload.overallScore
          }
        });
      }
    },
    
    resetAnalysis: (state) => {
      state.currentEmail = null;
      state.results = [];
      state.isAnalyzing = false;
      state.currentStepId = null;
      state.progress = 0;
      state.error = null;
      
      logger.info('Analysis state reset', { module: 'analysis' });
    },
    
    clearHistory: (state) => {
      state.history = [];
      logger.info('Analysis history cleared', { module: 'analysis' });
    },
  },
});

export const {
  setCurrentEmail,
  clearCurrentEmail,
  startAnalysis,
  setCurrentStep,
  updateProgress,
  addResult,
  setAnalysisError,
  completeAnalysis,
  resetAnalysis,
  clearHistory,
} = analysisSlice.actions;

export default analysisSlice.reducer; 